// istanbul ignore file
'use client';

import Box from '@mui/material/Box';
import { styled } from '@mui/system';

import { useHasBrandEngineFeatureFlag } from 'reservations/account/hooks/featureFlags';
import { useAccount } from 'reservations/account/providers/AccountProvider';
import { CONTAINER_MAX_WIDTH } from 'reservations/base/helpers';
import { getStyledTopMatterContainerMaxWidth } from 'reservations/booking/helpers/index';
import muiPropForwarding from 'reservations/brand/helpers/muiPropForwarding';

// istanbul ignore file

type StyledTopMatterContainerProps = {
  hasImage: boolean;
  topMatterMaxWidth: string;
  hasBrandEngineFeature: boolean;
};

const StyledTopMatterContainer = styled(Box, {
  shouldForwardProp: muiPropForwarding(
    'hasImage',
    'topMatterMaxWidth',
    'hasBrandEngineFeature'
  )
})<StyledTopMatterContainerProps>(
  ({ theme, hasImage, topMatterMaxWidth, hasBrandEngineFeature }) => ({
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
      bottom: hasBrandEngineFeature ? theme.spacing(2) : 0,
      marginBottom: hasImage && !hasBrandEngineFeature ? '-80px' : 0,
      maxWidth: CONTAINER_MAX_WIDTH
    },
    [theme.breakpoints.up('md')]: {
      bottom: hasBrandEngineFeature
        ? theme.spacing(2)
        : hasImage
          ? theme.spacing(6)
          : 0,
      display: 'flex',
      alignItems: 'center',
      maxWidth: hasBrandEngineFeature ? CONTAINER_MAX_WIDTH : topMatterMaxWidth
    },
    margin: '0 auto',
    maxWidth: topMatterMaxWidth,
    position: 'relative'
  })
);

type BaseTopMatterProps = {
  children?: React.ReactNode;
};

const BaseTopMatter = ({ children }: BaseTopMatterProps) => {
  const { hasImage } = useAccount();

  const hasBrandEngineFeature = useHasBrandEngineFeatureFlag();

  const topMatterMaxWidth = getStyledTopMatterContainerMaxWidth(
    window.location.pathname,
    hasBrandEngineFeature
  );

  return (
    <Box id="base-top-matter-container">
      <Box
        sx={theme => ({
          opacity: '0.05',
          position: 'relative',
          minHeight: theme.spacing(10),
          backgroundColor: theme.palette.primary.main,
          visibility: hasBrandEngineFeature ? 'hidden' : 'visible'
        })}
        data-testid="base-top-matter-strip"
      />
      <StyledTopMatterContainer
        hasImage={hasImage}
        topMatterMaxWidth={topMatterMaxWidth}
        hasBrandEngineFeature={hasBrandEngineFeature}
      >
        {children}
      </StyledTopMatterContainer>
    </Box>
  );
};

export default BaseTopMatter;
